/* AddProductionAssetModal.js — founder ask May 16, 2026
 * ─────────────────────────────────────────────────────────────
 * Quick-add modal for PRODUCTION-ONLY assets — props the client
 * never reviewed but production needs anyway (e.g. background
 * dressing, generic crowd items). These assets:
 *   • Skip the `concept_art` stage entirely (no client approval gate)
 *   • Land directly at `asset_build` status="unassigned" so the PM
 *     can allot them to an artist on the same tab they're already on
 *   • Are flagged `production_only: true` on master_library so
 *     client-facing MDL queries can hide them.
 *
 * Wraps POST /api/projects/{project_id}/add-single-asset with the
 * `production_only: true` body — that endpoint already exists and
 * was extended (server.py:3596) to honour the flag.
 */
import React, { useState } from 'react';
import { Plus, X, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORIES = [
  { value: 'character',  label: 'Character' },
  { value: 'prop',       label: 'Prop' },
  { value: 'set',        label: 'Set / Environment' },
  { value: 'vehicle',    label: 'Vehicle' },
  { value: 'fx',         label: 'FX Element' },
];

export default function AddProductionAssetModal({ projectId, onClose, onCreated }) {
  const { api } = useAuth();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('prop');
  // Optional fields — keep the modal minimal; producers can edit
  // technical_notes / complexity later on the MDL page.
  const [notes, setNotes] = useState('');
  const [rigRequired, setRigRequired] = useState(false);
  // Founder's headline ask is "skip client approval" — production_only
  // is on by default. Producers can untick it if they DO want this
  // asset to pass through concept review for some reason.
  const [productionOnly, setProductionOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!name.trim()) { setErr('Asset name is required.'); return; }
    if (!projectId) { setErr('No project selected.'); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/master-library/asset`, {
        name: name.trim(),
        category,
        description: notes.trim(),
        rig_required: rigRequired,
        production_only: productionOnly,
      });
      onCreated?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to add asset');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-production-asset-modal"
    >
      <div
        className="bg-zinc-950 border border-zinc-700 rounded-lg w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-cyan-400" />
            <h3 className="text-[14px] font-semibold text-zinc-100">Add Asset</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
              Asset name
            </label>
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Generic Lamp Post, Forest Foliage Set #2"
              data-testid="add-asset-name"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) submit(); }}
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-[13px] text-zinc-100 placeholder-zinc-600 focus:border-cyan-400 outline-none"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
              Category
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-testid="add-asset-category"
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-[13px] text-zinc-100 focus:border-cyan-400 outline-none"
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {category === 'character' && (
            <label className="flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={rigRequired}
                onChange={e => setRigRequired(e.target.checked)}
                data-testid="add-asset-rig-required"
                className="accent-cyan-400"
              />
              Needs a rig (auto-spawns rigging task after asset build)
            </label>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Brief description / production context"
              data-testid="add-asset-notes"
              className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-[12px] text-zinc-200 placeholder-zinc-600 focus:border-cyan-400 outline-none resize-none"
            />
          </div>

          <label className="flex items-start gap-2 bg-cyan-500/5 border border-cyan-500/30 rounded px-3 py-2 cursor-pointer"
                 data-testid="add-asset-production-only-row">
            <input
              type="checkbox"
              checked={productionOnly}
              onChange={e => setProductionOnly(e.target.checked)}
              data-testid="add-asset-production-only"
              className="mt-0.5 accent-cyan-400"
            />
            <div className="flex-1">
              <div className="text-[12px] text-cyan-200 font-medium">
                Production-only (skip client approval)
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                Bypasses the concept-art gate. Asset lands directly at
                Asset Build, unassigned, ready for artist allotment. Won't
                appear in client-facing reviews or portfolios.
              </div>
            </div>
          </label>

          {err && (
            <div className="flex items-center gap-1.5 text-[11px] text-rose-300" data-testid="add-asset-error">
              <AlertCircle className="w-3 h-3" /> {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-[11px] text-zinc-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !name.trim()}
            data-testid="add-asset-submit"
            className="px-4 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-zinc-950 text-[11px] font-bold rounded flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            {busy ? 'Adding…' : 'Add Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}
